import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { ApiError } from '@/api/client'
import { adminApi, workspacesApi } from '@/api/workspaces'
import { RefreshFailed } from '@/components/RefreshFailed'
import { adminCopy, settingsCopy } from '@/copy'
import { useAuthStore } from '@/store/auth'
import { useToastStore } from '@/store/toast'
import type { UserInfo, WorkspaceInfo } from '@/types/api'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { ConfirmDialog } from '@/ui/Modal/Modal'
import { RowList } from '@/ui/RowList/RowList'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { WorkspaceRow } from './WorkspaceRow'

export function WorkspacesTab() {
  const qc = useQueryClient()
  const addToast = useToastStore((s) => s.addToast)
  const userId = useAuthStore((s) => s.userId)
  const isSystemAdmin = useAuthStore((s) => s.isSystemAdmin)
  const [expandedWorkspace, setExpandedWorkspace] = useState<string | null>(
    null,
  )
  const [confirmDeleteWorkspaceId, setConfirmDeleteWorkspaceId] = useState<
    string | null
  >(null)
  const [deletingWorkspaceIds, setDeletingWorkspaceIds] = useState<Set<string>>(
    new Set(),
  )
  const [pendingDeletionIds, setPendingDeletionIds] = useState<Set<string>>(
    new Set(),
  )
  const retryTimers = useRef(
    new Map<string, ReturnType<typeof globalThis.setTimeout>>(),
  )

  const workspacesQuery = useQuery<WorkspaceInfo[]>({
    queryKey: ['admin', 'workspaces'],
    queryFn: adminApi.listAllWorkspaces,
  })
  const workspaces = workspacesQuery.data ?? []
  const showSkeleton = useSkeletonDelay(workspacesQuery.isPending)
  const workspaceToDelete = workspaces.find(
    (ws) => ws.workspace_id === confirmDeleteWorkspaceId,
  )

  const { data: allUsers = [] } = useQuery<UserInfo[]>({
    queryKey: ['admin', 'users'],
    queryFn: adminApi.listUsers,
  })

  useEffect(
    () => () => {
      retryTimers.current.forEach((timer) => {
        globalThis.clearTimeout(timer)
      })
      retryTimers.current.clear()
    },
    [],
  )

  function clearRetry(workspaceId: string) {
    const timer = retryTimers.current.get(workspaceId)
    if (timer) globalThis.clearTimeout(timer)
    retryTimers.current.delete(workspaceId)
  }

  async function runDelete(workspaceId: string, wasPending = false) {
    clearRetry(workspaceId)
    setDeletingWorkspaceIds((current) => new Set(current).add(workspaceId))
    try {
      const response = await workspacesApi.deleteWithMeta(workspaceId)
      setConfirmDeleteWorkspaceId(null)
      if (response.status === 202) {
        setPendingDeletionIds((current) => new Set(current).add(workspaceId))
        qc.setQueryData<WorkspaceInfo[]>(
          ['admin', 'workspaces'],
          (current = []) =>
            current.map((workspace) =>
              workspace.workspace_id === workspaceId
                ? { ...workspace, lifecycle_status: 'deleting' }
                : workspace,
            ),
        )
        if (!wasPending) {
          addToast(settingsCopy.workspaceDelete.committed, 'info')
        }
        const delay = Math.max(250, (response.retryAfterSeconds ?? 1) * 1_000)
        const timer = globalThis.setTimeout(() => {
          retryTimers.current.delete(workspaceId)
          void runDelete(workspaceId, true)
        }, delay)
        retryTimers.current.set(workspaceId, timer)
      } else {
        setPendingDeletionIds((current) => {
          const next = new Set(current)
          next.delete(workspaceId)
          return next
        })
        qc.setQueryData<WorkspaceInfo[]>(
          ['admin', 'workspaces'],
          (current = []) =>
            current.filter(
              (workspace) => workspace.workspace_id !== workspaceId,
            ),
        )
        void qc.invalidateQueries({ queryKey: ['admin', 'workspaces'] })
      }
    } catch (error) {
      if (!wasPending) {
        setPendingDeletionIds((current) => {
          const next = new Set(current)
          next.delete(workspaceId)
          return next
        })
      }
      addToast(
        settingsCopy.workspaceDelete.failure(
          error instanceof ApiError ? error.status : undefined,
        ),
        'error',
      )
    } finally {
      setDeletingWorkspaceIds((current) => {
        const next = new Set(current)
        next.delete(workspaceId)
        return next
      })
    }
  }

  return (
    <>
      <SectionHeader rule trailingSpacing="section">
        {adminCopy.workspaces.heading}
      </SectionHeader>
      {workspacesQuery.isPending ? (
        <div aria-busy="true">{showSkeleton && <Skeleton lines={3} />}</div>
      ) : workspacesQuery.isError && !workspacesQuery.data ? (
        <Alert tone="danger">
          <Inline gap="space-8" align="baseline" wrap>
            <Text>{adminCopy.loadFailed.workspaces}</Text>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void workspacesQuery.refetch()}
            >
              {adminCopy.retry}
            </Button>
          </Inline>
        </Alert>
      ) : (
        <Stack gap="space-12">
          {workspacesQuery.isError && (
            <RefreshFailed
              message={adminCopy.refreshFailed.workspaces}
              onRetry={() => void workspacesQuery.refetch()}
            />
          )}
          {workspaces.length === 0 ? (
            <EmptyState
              variant="inline"
              description={adminCopy.workspaces.empty}
            />
          ) : (
            <RowList aria-label={adminCopy.workspaces.listLabel}>
              {workspaces.map((ws) => (
                <WorkspaceRow
                  key={ws.workspace_id}
                  workspace={ws}
                  allUsers={allUsers}
                  expanded={expandedWorkspace === ws.workspace_id}
                  currentUserId={userId}
                  canManageMembers={
                    (ws.management_kind ?? 'user') === 'user' &&
                    (isSystemAdmin || ws.role === 'owner')
                  }
                  canTransferOwnership={ws.role === 'owner'}
                  canDelete={
                    (ws.management_kind ?? 'user') === 'user' &&
                    ws.role === 'owner'
                  }
                  deletionPending={
                    pendingDeletionIds.has(ws.workspace_id) ||
                    ws.lifecycle_status === 'deleting'
                  }
                  isDeleting={deletingWorkspaceIds.has(ws.workspace_id)}
                  onToggle={() =>
                    setExpandedWorkspace((prev) =>
                      prev === ws.workspace_id ? null : ws.workspace_id,
                    )
                  }
                  onRequestDelete={() =>
                    setConfirmDeleteWorkspaceId(ws.workspace_id)
                  }
                  onRetryDelete={() => void runDelete(ws.workspace_id, true)}
                />
              ))}
            </RowList>
          )}
        </Stack>
      )}

      <ConfirmDialog
        open={confirmDeleteWorkspaceId !== null}
        title={settingsCopy.workspaceDelete.title}
        tone="danger"
        confirmText={workspaceToDelete?.name}
        description={
          workspaceToDelete
            ? settingsCopy.workspaceDelete.describeNamed(workspaceToDelete.name)
            : settingsCopy.workspaceDelete.description
        }
        confirmLabel={settingsCopy.workspaceDelete.confirm}
        loading={
          confirmDeleteWorkspaceId
            ? deletingWorkspaceIds.has(confirmDeleteWorkspaceId)
            : false
        }
        onConfirm={() => {
          if (confirmDeleteWorkspaceId) {
            void runDelete(confirmDeleteWorkspaceId)
          }
        }}
        onClose={() => setConfirmDeleteWorkspaceId(null)}
      />
    </>
  )
}
